import React, { Component } from 'react'
import axios from 'axios'
import { Modal, Button, Select, Form, Spin, Transfer, Tree, Input, Icon, message } from 'antd'

const { Option } = Select

const PRIORITY_OPTIONS = [
  { value: 100, label: 'Priority 1 (100%)' },
  { value: 70, label: 'Priority 2 (70%)' },
  { value: 50, label: 'Priority 3 (50%)' },
  { value: 40, label: 'Priority 4 (40%)' },
]

class PriorityManagementModal extends Component {
  state = {
    priorityValue: undefined,
    allMaterials: [],
    selectedMaterialId: undefined,
    transferDataSource: [],  // flat [{key, title}] for Transfer
    plantInfoMap: {},        // plant_id → plant info (hierarchy)
    plantKeySet: new Set(),
    selectedPlantKeys: [],   // targetKeys (right panel)
    searchText: '',
    searchTextRight: '',
    loadingMaterials: false,
    loadingPlants: false,
    saving: false,
  }

  _isMounted = false

  componentDidMount() {
    this._isMounted = true
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) {
      const { initialData, accessToken } = this.props
      const priority = initialData ? initialData.priorityValue : null
      const materialId = initialData ? initialData.materialId : null

      this.setState({
        priorityValue: priority || undefined,
        allMaterials: [],
        selectedMaterialId: materialId || undefined,
        transferDataSource: [],
        plantInfoMap: {},
        plantKeySet: new Set(),
        selectedPlantKeys: [],
        searchText: '',
        searchTextRight: '',
        loadingMaterials: true,
        loadingPlants: !!initialData,
      })

      const headers = { Authorization: `Bearer ${accessToken}` }
      const base = process.env.REACT_APP_LARAVEL_API_ENDPOINT

      if (initialData) {
        // Edit mode: โหลด materials + plants พร้อมกัน (filter ตาม material + priority)
        Promise.all([
          axios.post(`${base}/plant-silo-priority/get-materials-by-priority`, {}, { headers }),
          axios.post(`${base}/plant-silo-priority/get-plants-by-material`,
            { auto_order_priority: priority, material_id: materialId },
            { headers }
          ),
        ])
          .then(([matRes, plantRes]) => {
            if (!this._isMounted) return
            const allPlants = plantRes.data.all_plants || []
            const selectedIds = (plantRes.data.selected_plant_ids || []).map(String)
            const transferDataSource = allPlants.map((p) => ({
              key: String(p.plant_id),
              title: `${p.plant_code} - ${p.plant_name}`,
            }))
            const plantInfoMap = {}
            allPlants.forEach((p) => { plantInfoMap[String(p.plant_id)] = p })
            this.setState({
              allMaterials: matRes.data.data || [],
              transferDataSource,
              plantInfoMap,
              plantKeySet: new Set(allPlants.map((p) => String(p.plant_id))),
              selectedPlantKeys: selectedIds,
              loadingMaterials: false,
              loadingPlants: false,
            })
          })
          .catch(() => {
            if (this._isMounted) this.setState({ loadingMaterials: false, loadingPlants: false })
          })
      } else {
        // Add mode: โหลด materials อย่างเดียว (โรงงานโหลดเมื่อเลือก material)
        axios.post(`${base}/plant-silo-priority/get-materials-by-priority`, {}, { headers })
          .then((matRes) => {
            if (this._isMounted) this.setState({ allMaterials: matRes.data.data || [], loadingMaterials: false })
          })
          .catch(() => {
            if (this._isMounted) this.setState({ loadingMaterials: false })
          })
      }
    }
  }

  handleClose = () => {
    this.setState({
      priorityValue: undefined,
      allMaterials: [],
      selectedMaterialId: undefined,
      transferDataSource: [],
      plantInfoMap: {},
      plantKeySet: new Set(),
      selectedPlantKeys: [],
      searchText: '',
      searchTextRight: '',
    })
    this.props.onClose()
  }

  handlePriorityChange = (value) => {
    this.setState({ priorityValue: value })
  }

  handleMaterialChange = (materialId) => {
    this.setState({
      selectedMaterialId: materialId,
      transferDataSource: [],
      plantInfoMap: {},
      plantKeySet: new Set(),
      selectedPlantKeys: [],
      loadingPlants: true,
    })

    const { accessToken } = this.props
    const headers = { Authorization: `Bearer ${accessToken}` }
    const base = process.env.REACT_APP_LARAVEL_API_ENDPOINT

    axios
      .post(`${base}/plant-silo-priority/get-plants-by-material`, { material_id: materialId }, { headers })
      .then((res) => {
        if (!this._isMounted) return
        const allPlants = res.data.all_plants || []
        const transferDataSource = allPlants.map((p) => ({
          key: String(p.plant_id),
          title: `${p.plant_code} - ${p.plant_name}`,
        }))
        const plantInfoMap = {}
        allPlants.forEach((p) => { plantInfoMap[String(p.plant_id)] = p })
        this.setState({
          transferDataSource,
          plantInfoMap,
          plantKeySet: new Set(allPlants.map((p) => String(p.plant_id))),
          selectedPlantKeys: [],
          loadingPlants: false,
        })
      })
      .catch(() => {
        if (this._isMounted) this.setState({ loadingPlants: false })
      })
  }

  // สร้าง tree structure จาก flat items array
  buildTreeFromItems = (items) => {
    const { plantInfoMap } = this.state
    const compMap = {}
    items.forEach((item) => {
      const p = plantInfoMap[item.key]
      if (!p) return

      const compId = p.company_id || 'none'
      if (compId === 'none') return  // ไม่แสดงโรงงานที่ไม่ระบุกิจการ
      const compKey = `__comp_${compId}`
      if (!compMap[compKey]) {
        compMap[compKey] = { title: p.company_name, key: compKey, children: {}, isNoComp: false }
      }
      let cur = compMap[compKey].children

      if (p.division_name) {
        const k = `__div_${compId}_${p.division_name}`
        if (!cur[k]) cur[k] = { title: p.division_name, key: k, children: {} }
        cur = cur[k].children
      }
      if (p.department_name) {
        const k = `__dept_${compId}_${p.division_name}_${p.department_name}`
        if (!cur[k]) cur[k] = { title: p.department_name, key: k, children: {} }
        cur = cur[k].children
      }
      if (p.section_name) {
        const k = `__sec_${compId}_${p.division_name}_${p.department_name}_${p.section_name}`
        if (!cur[k]) cur[k] = { title: p.section_name, key: k, children: {} }
        cur = cur[k].children
      }

      cur[item.key] = { title: item.title, key: item.key, isLeaf: true }
    })

    const toArray = (map, depth) =>
      Object.values(map).map((node) => ({
        title: node.title,
        key: node.key,
        isLeaf: node.isLeaf || false,
        depth: depth,
        children: node.children && Object.keys(node.children).length > 0
          ? toArray(node.children, depth + 1)
          : undefined,
      }))

    return toArray(compMap, 0)
  }

  // เก็บ keys ที่ depth 0 (กิจการ) และ depth 1 (ฝ่ายส่วน) สำหรับ expand
  getExpandedKeys = (treeData, maxDepth = 1) => {
    const keys = []
    const traverse = (nodes, depth) => {
      if (depth > maxDepth) return
      nodes.forEach((node) => {
        if (!node.isLeaf) {
          keys.push(node.key)
          if (node.children) traverse(node.children, depth + 1)
        }
      })
    }
    traverse(treeData, 0)
    return keys
  }

  handleSave = () => {
    const { priorityValue, selectedMaterialId, selectedPlantKeys } = this.state
    if (!priorityValue) { message.warning('กรุณาเลือก Priority'); return }
    if (!selectedMaterialId) { message.warning('กรุณาเลือกวัตถุดิบ'); return }
    if (selectedPlantKeys.length === 0) { message.warning('กรุณาเลือกโรงงาน'); return }

    this.setState({ saving: true })
    axios
      .post(
        `${process.env.REACT_APP_LARAVEL_API_ENDPOINT}/plant-silo-priority/bulk-update`,
        {
          plant_ids: selectedPlantKeys.map(Number),
          material_ids: [Number(selectedMaterialId)],
          auto_order_priority: priorityValue,
        },
        { headers: { Authorization: `Bearer ${this.props.accessToken}` } }
      )
      .then((res) => {
        if (this._isMounted) {
          this.setState({ saving: false })
          message.success(`อัพเดท Priority สำเร็จ (${res.data.affected_rows} รายการ)`)
          this.props.onClose()
        }
      })
      .catch(() => {
        if (this._isMounted) {
          this.setState({ saving: false })
          message.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
        }
      })
  }

  render() {
    const {
      priorityValue,
      allMaterials,
      selectedMaterialId,
      transferDataSource,
      plantKeySet,
      selectedPlantKeys,
      searchText,
      searchTextRight,
      loadingMaterials,
      loadingPlants,
      saving,
    } = this.state
    const { visible } = this.props

    return (
      <Modal
        title='จัดการ Priority ของแต่ละโรงงาน'
        visible={visible}
        onCancel={this.handleClose}
        width={1000}
        footer={[
          <Button key='cancel' onClick={this.handleClose}>ยกเลิก</Button>,
          <Button key='save' type='primary' loading={saving} disabled={selectedPlantKeys.length === 0} onClick={this.handleSave}>บันทึก</Button>,
        ]}
      >
        <Spin spinning={loadingMaterials || loadingPlants || saving}>

          {/* 1. Priority */}
          <Form.Item label='Priority' required style={{ marginBottom: 16 }}>
            <Select
              placeholder='กรุณาเลือก Priority'
              value={priorityValue}
              onChange={this.handlePriorityChange}
              style={{ width: '100%' }}
            >
              {PRIORITY_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* 2. วัตถุดิบ */}
          <Form.Item label='วัตถุดิบ' required style={{ marginBottom: 16 }}>
            <Select
              placeholder='กรุณาเลือกวัตถุดิบ'
              value={selectedMaterialId}
              onChange={this.handleMaterialChange}
              disabled={!!this.props.initialData}
              showSearch
              optionFilterProp='children'
              style={{ width: '100%' }}
            >
              {allMaterials.map((m) => (
                <Option key={m.material_id} value={m.material_id}>
                  {m.material_code} - {m.material_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 3. โรงงาน - Transfer + Tree */}
          <div>
              <span style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>เลือกโรงงาน:</span>
              <Transfer
                dataSource={transferDataSource}
                targetKeys={selectedPlantKeys}
                onChange={(nextTargetKeys) => this.setState({ selectedPlantKeys: nextTargetKeys })}
                titles={['โรงงานทั้งหมด', 'โรงงานที่เลือก']}
                listStyle={{ width: 420, height: 430 }}
                render={(item) => item.title}
              >
                {({ direction, filteredItems }) => {
                  const isLeft = direction === 'left'
                  const currentSearch = isLeft ? searchText : searchTextRight
                  const searchKey = isLeft ? 'searchText' : 'searchTextRight'

                  const searchLower = currentSearch.toLowerCase()
                  const visibleItems = currentSearch
                    ? filteredItems.filter((i) => i.title.toLowerCase().indexOf(searchLower) > -1)
                    : filteredItems

                  const treeData = this.buildTreeFromItems(visibleItems)
                  // ถ้าค้นหา → expand ทั้งหมด, ถ้าไม่ค้นหา → expand เฉพาะ กิจการ + ฝ่ายส่วน
                  const expandedKeys = currentSearch
                    ? this.getExpandedKeys(treeData, Infinity)
                    : this.getExpandedKeys(treeData, 1)

                  // ฝั่งซ้าย: ไม่มี check (items ยังไม่ถูกเลือก)
                  // ฝั่งขวา: check ทุก item ที่แสดง (ทุกอันอยู่ใน targetKeys แล้ว)
                  const checkedKeys = isLeft ? [] : visibleItems.map((i) => i.key)

                  const handleCheck = (checkedKeys) => {
                    const checkedLeafKeys = checkedKeys.filter((k) => plantKeySet.has(k))
                    const visibleSet = new Set(visibleItems.map((i) => i.key))

                    if (isLeft) {
                      // เพิ่ม checked items เข้า targetKeys ทันที
                      const newTarget = Array.from(new Set([...selectedPlantKeys, ...checkedLeafKeys]))
                      this.setState({ selectedPlantKeys: newTarget })
                    } else {
                      // เก็บเฉพาะ checked items ไว้ใน targetKeys (uncheck = เอาออก)
                      const keptFromOthers = selectedPlantKeys.filter((k) => !visibleSet.has(k))
                      this.setState({ selectedPlantKeys: [...keptFromOthers, ...checkedLeafKeys] })
                    }
                  }

                  return (
                    <div style={{ padding: '0 8px 8px' }}>
                      <Input
                        placeholder='ค้นหาโรงงาน...'
                        prefix={<Icon type='search' />}
                        value={currentSearch}
                        onChange={(e) => this.setState({ [searchKey]: e.target.value })}
                        allowClear
                        style={{ marginBottom: 8 }}
                      />
                      <div style={{ overflow: 'auto', height: 335 }}>
                        {treeData.length > 0 ? (
                          <Tree
                            key={currentSearch}
                            checkable
                            defaultExpandedKeys={expandedKeys}
                            checkedKeys={checkedKeys}
                            onCheck={handleCheck}
                            treeData={treeData}
                          />
                        ) : (
                          <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>ไม่พบข้อมูล</div>
                        )}
                      </div>
                    </div>
                  )
                }}
              </Transfer>
            </div>

        </Spin>
      </Modal>
    )
  }
}

export default PriorityManagementModal
